import { useState, useCallback, useRef, useEffect } from 'react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { enqueueMutation, getQueueSize } from '@/lib/syncQueue';
import { putStoreItem, deleteStoreItem, getStoreData, putStoreBulk, getStoreFreshness, formatFreshness } from '@/lib/offlineDb';

type TableName =
  | 'patients'
  | 'patients_temporaires'
  | 'rendez_vous'
  | 'consultations'
  | 'prescriptions'
  | 'resultats_analyses'
  | 'medecins'
  | 'secretaires'
  | 'laborantins'
  | 'sage_femmes'
  | 'infirmiers'
  | 'comptables'
  | 'medicaments'
  | 'actualites'
  | 'dossiers_analyses'
  | 'suivis_patients'
  | 'fiches_surveillance'
  | 'fiches_soins'
  | 'bons_laboratoire'
  | 'disponibilites_medecins'
  | 'dossiers_roses'
  | 'file_attente'
  | 'assurances'
  | 'patient_assurances'
  | 'notifications'
  | 'messages'
  | 'corrections_temporaires';

interface FreshnessInfo {
  lastSyncedAt: number | null;
  ageMs: number | null;
  isStale: boolean;
  recordCount: number;
  formattedLabel: string | null;
}

interface MutationState<T> {
  loading: boolean;
  error: string | null;
  success: boolean;
  data: T | null;
  isOffline: boolean;
  pendingSync: boolean;
}

interface UseOfflineMutationReturn<T> {
  mutate: (params: {
    table: TableName;
    operation: 'INSERT' | 'UPDATE' | 'DELETE';
    recordId?: string;
    data?: Record<string, any>;
    onlineExecutor?: () => Promise<T>;
    metadata?: { summary?: string; performedBy?: string };
    mirrorToCache?: boolean;
    /** Timestamp updated_at attendu côté serveur (pour détection conflits cross-device).
     *  À capturer au moment du chargement du formulaire, pas à la soumission.
     *  Si le serveur a un updated_at différent au moment du replay → CROSS_DEVICE_CONFLICT */
    expectedUpdatedAt?: string | null;
    /** ID de la mutation parente pour chaînage FK INSERT → INSERT */
    parentMutationId?: string;
  }) => Promise<T>;
  state: MutationState<T>;
  resetState: () => void;
}

/**
 * Hook pour envelopper n'importe quelle mutation Supabase avec le système offline.
 *
 * - Online : exécute normalement, puis sauvegarde dans IndexedDB
 * - Offline : met dans la queue de sync + sauvegarde dans IndexedDB
 *
 * Usage typique :
 *   const { mutate, state } = useOfflineMutation<any>();
 *   await mutate({
 *     table: 'rendez_vous',
 *     operation: 'INSERT',
 *     data: { patient_id: '...', medecin_id: '...', ... },
 *     onlineExecutor: async () => {
 *       const { data } = await supabase.from('rendez_vous').insert({...}).select().single();
 *       return data;
 *     },
 *     metadata: { summary: 'RDV Dr. X - 12/08' }
 *   });
 */
export function useOfflineMutation<T = any>(): UseOfflineMutationReturn<T> {
  const { isOnline } = useOnlineStatus();
  const [state, setState] = useState<MutationState<T>>({
    loading: false,
    error: null,
    success: false,
    data: null,
    isOffline: false,
    pendingSync: false,
  });
  const mountedRef = useRef(true);
  // Dernier ID inséré (pour les INSERT offline, on génère un ID temporaire)
  const pendingIdRef = useRef<string | null>(null);

  const resetState = useCallback(() => {
    if (!mountedRef.current) return;
    setState({
      loading: false,
      error: null,
      success: false,
      data: null,
      isOffline: false,
      pendingSync: false,
    });
  }, []);

  const mutate = useCallback(async (params: {
    table: TableName;
    operation: 'INSERT' | 'UPDATE' | 'DELETE';
    recordId?: string;
    data?: Record<string, any>;
    onlineExecutor?: () => Promise<T>;
    metadata?: { summary?: string; performedBy?: string };
    mirrorToCache?: boolean;
    expectedUpdatedAt?: string | null;
    parentMutationId?: string;
  }): Promise<T> => {
    const {
      table,
      operation,
      recordId,
      data,
      onlineExecutor,
      metadata,
      mirrorToCache = true,
      expectedUpdatedAt,
      parentMutationId,
    } = params;

    if (!mountedRef.current) {
      throw new Error('Component unmounted');
    }

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
      success: false,
      isOffline: false,
      pendingSync: false,
    }));

    // Générer un ID temporaire pour les INSERT offline
    const effectiveId = recordId || (operation === 'INSERT' ? `pending-${Date.now()}-${Math.random().toString(36).substring(2, 9)}` : '');

    try {
      // ===== MODE ONLINE =====
      if (isOnline && onlineExecutor) {
        const result = await onlineExecutor();

        // Miroir dans IndexedDB
        if (mirrorToCache && result) {
          if (operation === 'DELETE') {
            await deleteStoreItem(table, effectiveId);
          } else {
            await putStoreItem(table, result);
          }
        }

        if (!mountedRef.current) return result;

        setState({
          loading: false,
          error: null,
          success: true,
          data: result,
          isOffline: false,
          pendingSync: false,
        });
        return result;
      }

      // ===== MODE OFFLINE =====
      // Enqueue dans la file de sync
      enqueueMutation(table, operation, effectiveId, data || undefined, metadata, parentMutationId, expectedUpdatedAt);

      // Miroir dans IndexedDB (données locales immédiatement dispo)
      if (mirrorToCache && data) {
        if (operation === 'DELETE') {
          await deleteStoreItem(table, effectiveId);
        } else {
          const cachedItem = { id: effectiveId, ...data, _offline: true };
          await putStoreItem(table, cachedItem);
        }
      }

      if (!mountedRef.current) {
        // Résoudre avec un placeholder
        return { id: effectiveId, _offline: true, _pendingSync: true } as unknown as T;
      }

      pendingIdRef.current = effectiveId;

      setState({
        loading: false,
        error: null,
        success: true,
        data: { id: effectiveId, _offline: true, _pendingSync: true } as unknown as T,
        isOffline: true,
        pendingSync: true,
      });

      return { id: effectiveId, _offline: true, _pendingSync: true } as unknown as T;
    } catch (err: any) {
      const errorMessage = err?.message || 'Erreur inconnue';

      if (!mountedRef.current) throw err;

      // Si l'erreur est due à une perte de connexion, basculer en mode offline
      if (
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('NetworkError') ||
        errorMessage.includes('network') ||
        errorMessage.includes('timeout') ||
        errorMessage.includes('abort')
      ) {
        // Fallback offline
        enqueueMutation(table, operation, effectiveId, data || undefined, metadata, parentMutationId, expectedUpdatedAt);

        if (mirrorToCache && data) {
          try {
            if (operation === 'DELETE') {
              await deleteStoreItem(table, effectiveId);
            } else {
              await putStoreItem(table, { id: effectiveId, ...data, _offline: true });
            }
          } catch {
            // Silencieux
          }
        }

        setState({
          loading: false,
          error: null,
          success: true,
          data: { id: effectiveId, _offline: true, _pendingSync: true } as unknown as T,
          isOffline: true,
          pendingSync: true,
        });

        return { id: effectiveId, _offline: true, _pendingSync: true } as unknown as T;
      }

      setState({
        loading: false,
        error: errorMessage,
        success: false,
        data: null,
        isOffline: false,
        pendingSync: false,
      });

      throw err;
    }
  }, [isOnline]);

  return { mutate, state, resetState };
}

/**
 * Hook pour lire des données avec fallback IndexedDB quand offline.
 *
 * Usage :
 *   const { data, loading, error, isOffline } = useOfflineQuery(
 *     'patients',
 *     async () => {
 *       const { data } = await supabase.from('patients').select('*');
 *       return data || [];
 *     }
 *   );
 */
export function useOfflineQuery<T>(
  storeName: TableName,
  fetcher: () => Promise<T[]>,
  deps: any[] = [],
): {
  data: T[];
  loading: boolean;
  error: string | null;
  isOffline: boolean;
  refresh: () => Promise<void>;
} {
  const { isOnline } = useOnlineStatus();
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoading(true);
    setError(null);

    try {
      if (isOnline) {
        // Essayer le réseau d'abord
        const result = await fetcher();
        if (!mountedRef.current) return;
        setData(result);
        setLoading(false);

        // Mettre en cache dans IndexedDB
        if (result && result.length > 0) {
          try {
            await putStoreBulk(storeName, result);
          } catch {
            // Silencieux
          }
        }
      } else {
        // Offline : lire depuis IndexedDB
        const cached = await getStoreData(storeName);
        if (!mountedRef.current) return;
        setData(cached as T[]);
        setLoading(false);
      }
    } catch (err: any) {
      if (!mountedRef.current) return;
      // Fallback : IndexedDB
      try {
        const cached = await getStoreData(storeName);
        setData(cached as T[]);
        setLoading(false);
      } catch {
        setError(err?.message || 'Erreur de chargement');
        setLoading(false);
      }
    }
  }, [isOnline, storeName, ...deps]);

  // Charger au mount et quand les deps changent
  // Note: on n'inclut pas 'load' dans les deps car c'est un useCallback
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadRef = useRef(load);
  loadRef.current = load;

  // Utiliser un effet séparé pour le chargement
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const depsKey = JSON.stringify(deps);
  useEffect(() => {
    loadRef.current();
  }, [isOnline, depsKey]);

  return { data, loading, error, isOffline: !isOnline, refresh: load };
}